<template>
  <Teleport to="body">
    <div v-if="isVisible" class="wild-choice-modal-overlay" @click.self="handleOverlayClick">
      <div class="wild-choice-modal">
      <!-- Header -->
      <div class="modal-header">
        <h3 class="modal-title">
          <span class="material-symbols-outlined">casino</span>
          Wild Canasta ({{ cardCount }} cards)
        </h3>
        <button
          v-if="closable"
          class="modal-close-btn"
          @click="handleCancel"
          aria-label="Close modal"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <!-- Info Section -->
      <div class="modal-body">
        <p class="choice-intro">
          You have {{ cardCount }} wild cards. How would you like to play this canasta?
        </p>

        <!-- Choice Cards -->
        <div class="choice-cards">
          <!-- Normal Wild Canasta -->
          <button class="choice-card normal-choice" @click="handleChoice('normal')">
            <div class="choice-icon">
              <span class="material-symbols-outlined">visibility</span>
            </div>
            <div class="choice-content">
              <h4 class="choice-title">Normal Wild Canasta</h4>
              <div class="choice-points">1,000 points</div>
              <ul class="choice-details">
                <li>Plays immediately to table</li>
                <li>Visible to all players</li>
                <li>Standard wild canasta bonus</li>
              </ul>
            </div>
          </button>

          <!-- Hidden Wild Canasta -->
          <button class="choice-card hidden-choice" @click="handleChoice('hidden')">
            <div class="choice-icon">
              <span class="material-symbols-outlined">visibility_off</span>
              <span class="h-badge">H</span>
            </div>
            <div class="choice-content">
              <h4 class="choice-title">Hidden Wild Canasta</h4>
              <div class="choice-points">Up to 2,000 points!</div>
              <ul class="choice-details">
                <li>Kept secret from opponents</li>
                <li>Auto-plays at 7 cards for 2,000 pts</li>
                <li>Reveal early for 1,500 pts</li>
                <li class="warning">Cannot go out while hidden</li>
              </ul>
            </div>
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button class="btn-secondary" @click="handleCancel">Cancel</button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * WildCanastaChoiceModal Component
 *
 * Modal shown when player attempts to play 7+ wild cards as a canasta.
 * Offers choice between normal wild canasta (1,000 pts) or hidden (up to 2,000 pts).
 *
 * Visibility is controlled by parent via v-if - no internal state needed.
 *
 * @component
 * @example
 * <WildCanastaChoiceModal
 *   :cardCount="7"
 *   @choice="handleWildChoice"
 *   @cancelled="handleCancelled"
 * />
 */

// ============================================================================
// Props & Emits
// ============================================================================

interface Props {
  isOpen: boolean
  cardCount: number
  closable?: boolean
}

interface Emits {
  (e: 'choice', type: 'normal' | 'hidden'): void
  (e: 'cancelled'): void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  closable: true,
})

const emit = defineEmits<Emits>()

// Debug logging
import { onMounted, watch, computed } from 'vue'

// Computed to ensure reactivity
const isVisible = computed(() => {
  console.warn('[WildCanastaChoiceModal] 🔍 isVisible computed called, props.isOpen =', props.isOpen)
  return props.isOpen
})

onMounted(() => {
  console.warn('[WildCanastaChoiceModal] 🎰 Component MOUNTED, isOpen:', props.isOpen, 'isVisible:', isVisible.value)
})
watch(() => props.isOpen, (newVal) => {
  console.warn('[WildCanastaChoiceModal] 🎰 isOpen changed to:', newVal, 'isVisible now:', isVisible.value)
}, { immediate: true })

// ============================================================================
// Methods
// ============================================================================

/**
 * Handle choice selection
 */
function handleChoice(type: 'normal' | 'hidden') {
  emit('choice', type)
}

/**
 * Handle overlay click (close if closable)
 */
function handleOverlayClick() {
  handleCancel()
}

/**
 * Handle cancel
 */
function handleCancel() {
  emit('cancelled')
}
</script>

<style>
/* Wild Canasta Choice Modal - NOT scoped because Teleport moves content outside component */
/* All selectors prefixed with .wild-choice-modal-overlay for specificity */

.wild-choice-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200000;
  padding: 16px;
  animation: wcm-fadeIn 0.2s ease-out;
}

@keyframes wcm-fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes wcm-slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.wild-choice-modal-overlay .wild-choice-modal {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 12px;
  padding: 24px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  animation: wcm-slideUp 0.3s ease-out;
}

.wild-choice-modal-overlay .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.wild-choice-modal-overlay .modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #a855f7;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wild-choice-modal-overlay .modal-title .material-symbols-outlined {
  font-size: 1.5rem;
}

.wild-choice-modal-overlay .modal-close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wild-choice-modal-overlay .modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.wild-choice-modal-overlay .modal-body {
  margin-bottom: 16px;
}

.wild-choice-modal-overlay .choice-intro {
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  margin: 0 0 16px;
}

.wild-choice-modal-overlay .choice-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.wild-choice-modal-overlay .choice-card {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.wild-choice-modal-overlay .choice-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.wild-choice-modal-overlay .choice-card.normal-choice {
  border-color: rgba(16, 185, 129, 0.3);
}

.wild-choice-modal-overlay .choice-card.normal-choice:hover {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.1);
}

.wild-choice-modal-overlay .choice-card.hidden-choice {
  border-color: rgba(168, 85, 247, 0.3);
}

.wild-choice-modal-overlay .choice-card.hidden-choice:hover {
  border-color: #a855f7;
  background: rgba(168, 85, 247, 0.1);
}

.wild-choice-modal-overlay .choice-icon {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wild-choice-modal-overlay .normal-choice .choice-icon {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.wild-choice-modal-overlay .hidden-choice .choice-icon {
  background: rgba(168, 85, 247, 0.2);
  color: #a855f7;
}

.wild-choice-modal-overlay .choice-icon .material-symbols-outlined {
  font-size: 2rem;
}

.wild-choice-modal-overlay .h-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #a855f7, #8b3dd6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  box-shadow: 0 2px 6px rgba(168, 85, 247, 0.4);
}

.wild-choice-modal-overlay .choice-content {
  text-align: center;
  width: 100%;
}

.wild-choice-modal-overlay .choice-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: white;
  margin: 0 0 4px;
}

.wild-choice-modal-overlay .choice-points {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 12px;
}

.wild-choice-modal-overlay .normal-choice .choice-points {
  color: #10b981;
}

.wild-choice-modal-overlay .hidden-choice .choice-points {
  color: #a855f7;
}

.wild-choice-modal-overlay .choice-details {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.wild-choice-modal-overlay .choice-details li {
  padding: 4px 0;
  padding-left: 16px;
  position: relative;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  line-height: 1.4;
}

.wild-choice-modal-overlay .choice-details li::before {
  content: '•';
  position: absolute;
  left: 0;
  font-weight: 700;
}

.wild-choice-modal-overlay .normal-choice .choice-details li::before {
  color: #10b981;
}

.wild-choice-modal-overlay .hidden-choice .choice-details li::before {
  color: #a855f7;
}

.wild-choice-modal-overlay .choice-details li.warning {
  color: #fbbf24;
}

.wild-choice-modal-overlay .choice-details li.warning::before {
  content: '!';
  color: #fbbf24;
}

.wild-choice-modal-overlay .modal-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.wild-choice-modal-overlay .btn-secondary {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.wild-choice-modal-overlay .btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
}

@media (max-width: 768px) {
  .wild-choice-modal-overlay .wild-choice-modal {
    padding: 16px;
    max-height: 95vh;
  }

  .wild-choice-modal-overlay .choice-cards {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .wild-choice-modal-overlay .choice-card {
    padding: 12px;
  }

  .wild-choice-modal-overlay .choice-icon {
    width: 50px;
    height: 50px;
  }

  .wild-choice-modal-overlay .choice-icon .material-symbols-outlined {
    font-size: 1.5rem;
  }
}
</style>
